import './styles/archives.scss';

import { InputButton, PageTitle } from '../components/Primitives';
import React, { useEffect, useMemo, useState } from 'react';

import { Breadcrumb } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { FooterBanner } from '../components/Layout';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import { PartsListing } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/ProductListing';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { graphql } from 'gatsby';
import usaFlag from '@progymedia/sbi-web-englander/src/images/backgrounds/usa-flag-background.jpg';
import usaFlagMobile from '@progymedia/sbi-web-englander/src/images/backgrounds/usa-flag-background-mobile.jpg';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useQueryArg } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useQueryArg';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query {
        parts: allProductsJson(
            filter: { category: { regex: "^/Englander/Heaters/" } }
            sort: { title: { en_CA: ASC } }
        ) {
            nodes {
                code
                id
                category
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                documents {
                    en_CA
                    en_US
                    fr_CA
                }
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                parts {
                    code
                    id
                    title {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                attributes {
                    available_country {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    custom_made {
                        group
                        name {
                            en_US
                            en_CA
                            fr_CA
                        }
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    serial_number_from {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    serial_number_to {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        cms: allProductsJson(filter: { code: { eq: "englander_pieces" } }) {
            nodes {
                ...cmsDataFragment
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        mountainView: allProductsJson(
            filter: { code: { regex: "/^englander_mountain-view-parts/" } }
        ) {
            nodes {
                code
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    content_wysiwyg {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function Archives({ data }) {
    const { t } = useTranslation('layout');
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useQueryArg('s');
    const products = data.parts.nodes;
    const isMobile = useMobileDetect().isMobile();
    const cms = data?.cms?.nodes?.[0];
    const { currentLocale } = usePageLocalization();
    const title = cms?.title[currentLocale];
    const description = cms?.description[currentLocale];
    const [footerImg, setFooterImg] = useState(
        isMobile ? usaFlagMobile : usaFlag
    );

    useEffect(() => {
        if (isMobile) {
            setFooterImg(usaFlagMobile);
        } else {
            setFooterImg(usaFlag);
        }
    }, [isMobile]);

    const filteredProducts = useMemo(
        () =>
            products.filter((product: any) => {
                const prodTitle = product.title?.[currentLocale]
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');
                const prodCode = product.code
                    ?.toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');
                const normalizedSearch = search
                    ?.toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');

                const partsStr = JSON.stringify(product?.parts)
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');

                /*  Le filtre par pays a été retirer suite à ce ticket: S5418-343 

                const displayInCurrentCountry =
                    (currentLocale.includes('CA') &&
                        product?.attributes?.available_country?.values.some(
                            (value: { [x: string]: string }) =>
                                value[currentLocale] !== 'United States'
                        )) ||
                    (currentLocale.includes('US') &&
                        product?.attributes?.available_country?.values.some(
                            (value: { [x: string]: string }) =>
                                value[currentLocale] !== 'Canada'
                        )); */
                return (
                    prodTitle?.includes(normalizedSearch) ||
                    partsStr.includes(normalizedSearch) ||
                    prodCode?.includes(normalizedSearch) /* &&
                    (displayInCurrentCountry ||
                        !product?.attributes?.available_country) */
                );
            }),
        [currentLocale, products, search]
    );

    useEffect(() => {
        setSearch(searchTerm);
    }, [searchTerm]);

    return (
        <Layout>
            <SEO
                title={cms?.attributes?.meta_title?.values[0][currentLocale]}
                description={
                    cms?.attributes?.meta_description?.values[0][currentLocale]
                }
            />

            <div className='container'>
                <div className='archives-page'>
                    <Breadcrumb
                        elements={[
                            { link: '/', name: t('layout:appliance.home') },
                            { link: '', name: title },
                        ]}
                    />
                    <PageTitle title={title} description={description} />
                    <div className='archive-header'>
                        <div>
                            <ArchiveSearch
                                search={search}
                                setSearch={setSearch}
                                setSearchTerm={setSearchTerm}
                            />
                        </div>
                        {(currentLocale == 'en_CA' ||
                            currentLocale == 'en_US') && (
                            <div className='mountain-view-parts'>
                                <div className='mountain-view-parts-title'>
                                    {
                                        data.mountainView.nodes[0].title[
                                            currentLocale
                                        ]
                                    }
                                </div>
                                <div
                                    className='mountain-view-parts-content'
                                    dangerouslySetInnerHTML={{
                                        __html: data.mountainView.nodes[0]
                                            .attributes.content_wysiwyg
                                            .values[0][currentLocale],
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <PartsListing products={filteredProducts} />
                </div>
            </div>
            <FooterBanner
                image={footerImg}
                text={t('footer.proudly-made-us')}
            />
        </Layout>
    );
}

function ArchiveSearch(props: {
    search: string;
    setSearch: any;
    setSearchTerm: any;
}) {
    const { setSearch, setSearchTerm, search } = props;
    const [localSearch, setLocalSearch] = useState(search);
    const { t } = useTranslation('pages');

    useEffect(() => {
        setLocalSearch(search);
    }, [search]);

    return (
        <div className='archives-search'>
            <h4>{t('archives.search-title')}</h4>
            <InputButton
                label={t('archives.search-btn')}
                value={localSearch}
                setValue={(value) => setLocalSearch(value)}
                onBtnClick={() => {
                    setSearch(localSearch);
                    setSearchTerm(localSearch);
                }}
                placeholder={t('archives.search-placeholder')}
                icon={faSearch}
            />
        </div>
    );
}
